import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import type {
  ServiceCatalogItem,
  ServiceCatalogCreateRequest,
  ServiceCatalogUpdateRequest,
} from '@techgitter/contracts';
import { FormField } from '../panel/FormField';
import { CHECKBOX_SX, PRIMARY_BTN_SX } from '../../styles/designSystem';

interface ServiceCatalogEditDialogProps {
  open: boolean;
  /** The row being edited, or null when creating. */
  service: ServiceCatalogItem | null;
  /** Existing service keys (lowercased) for client-side duplicate warning. */
  existingKeys: string[];
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (
    payload: ServiceCatalogCreateRequest | ServiceCatalogUpdateRequest,
    serviceId: string | null,
  ) => void;
}

export const ServiceCatalogEditDialog: React.FC<ServiceCatalogEditDialogProps> = ({
  open,
  service,
  existingKeys,
  isSaving,
  error,
  onClose,
  onSave,
}) => {
  const isEdit = service !== null;
  const [serviceKey, setServiceKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  // Reseed the form whenever the dialog opens for a (possibly different) row.
  useEffect(() => {
    if (!open) return;
    setServiceKey(service?.serviceKey ?? '');
    setDisplayName(service?.displayName ?? '');
    setSortOrder(service?.sortOrder ?? 0);
    setIsActive(service?.isActive ?? true);
  }, [open, service]);

  const trimmedKey = serviceKey.trim();
  const trimmedName = displayName.trim();
  const isDuplicate =
    trimmedKey.length > 0 &&
    existingKeys.includes(trimmedKey.toLowerCase()) &&
    trimmedKey.toLowerCase() !== service?.serviceKey.toLowerCase();
  const isValid = trimmedKey.length > 0 && trimmedName.length > 0 && !isDuplicate;

  const handleSave = () => {
    if (!isValid) return;
    if (isEdit) {
      const payload: ServiceCatalogUpdateRequest = {
        serviceKey: trimmedKey,
        displayName: trimmedName,
        sortOrder,
        isActive,
      };
      onSave(payload, service!.serviceId);
    } else {
      const payload: ServiceCatalogCreateRequest = {
        serviceKey: trimmedKey,
        displayName: trimmedName,
        sortOrder,
        isActive,
      };
      onSave(payload, null);
    }
  };

  return (
    <Dialog open={open} onClose={() => !isSaving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
        {isEdit ? 'Edit service' : 'Add service'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormField
            label="Service Key"
            error={isDuplicate ? 'A service with this key already exists' : undefined}
          >
            <input
              value={serviceKey}
              placeholder="e.g. M365, backup, acme_bespoke"
              onChange={(e) => setServiceKey(e.target.value)}
              style={{ fontFamily: 'monospace' }}
              autoFocus
            />
          </FormField>
          <Box sx={{ fontSize: 12, color: '#888', mt: -1 }}>
            Used to match customers to ticket-type templates. Keep it distinctive —
            renaming it detaches templates and customer services that reference the old key.
          </Box>

          <FormField label="Display Name">
            <input
              value={displayName}
              placeholder="Human-friendly name shown in the picker"
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </FormField>

          <FormField label="Sort Order">
            <input
              type="number"
              value={String(sortOrder)}
              onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
            />
          </FormField>

          <FormControlLabel
            control={
              <Checkbox
                sx={CHECKBOX_SX}
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label="Active"
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving} sx={{ color: '#888' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disableElevation
          disabled={!isValid || isSaving}
          sx={PRIMARY_BTN_SX}
        >
          {isSaving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
